import React, { useState, useEffect, useMemo } from "react";
import {
  collection,
  query,
  onSnapshot,
  updateDoc,
  doc,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import { Card, CardHeader, CardContent, CardTitle } from "../components/Card";
import { useNavigate } from "react-router-dom";
import moment from "moment";

const ITEMS_PER_PAGE = parseInt(process.env.REACT_APP_ITEMS_PER_PAGE, 10) || 20;

// Fonction pour normaliser les chaînes (enlever les accents)
const normalizeString = (str) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

export default function UsersAdmin() {
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterVerified, setFilterVerified] = useState("all");
  const [filterActive, setFilterActive] = useState("all");

  useEffect(() => {
    const q = query(
      collection(db, "users"),
      orderBy("createdAt", "desc"),
      limit(1000)
    );
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const items = [];
      querySnapshot.forEach((d) => items.push({ id: d.id, ...d.data() }));
      setAllUsers(items);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let filtered = allUsers;

    // Filtre de recherche avec normalisation des accents
    const st = normalizeString(searchTerm);
    if (st) {
      filtered = filtered.filter(
        (u) =>
          normalizeString(u.firstname).includes(st) ||
          normalizeString(u.lastname).includes(st) ||
          normalizeString(u.email).includes(st) ||
          normalizeString(u.city).includes(st) ||
          normalizeString(u.department).includes(st)
      );
    }

    // Filtre vérifié - CORRECTION ICI: utiliser emailIsVerified
    if (filterVerified === "verified") {
      filtered = filtered.filter((u) => u.emailIsVerified);
    } else if (filterVerified === "notVerified") {
      filtered = filtered.filter((u) => !u.emailIsVerified);
    }

    // Filtre actif
    if (filterActive === "active") {
      filtered = filtered.filter((u) => u.isActive);
    } else if (filterActive === "inactive") {
      filtered = filtered.filter((u) => !u.isActive);
    }

    setFilteredUsers(filtered);
    setPage(1);
  }, [searchTerm, allUsers, filterVerified, filterActive]);

  const pageCount = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const currentUsers = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredUsers.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredUsers, page]);

  const updateUserLocal = (id, changes) => {
    setAllUsers((old) =>
      old.map((u) => (u.id === id ? { ...u, ...changes } : u))
    );
    setFilteredUsers((old) =>
      old.map((u) => (u.id === id ? { ...u, ...changes } : u))
    );
  };

  const toggleActive = async (user) => {
    const userRef = doc(db, "users", user.id);
    await updateDoc(userRef, { isActive: !user.isActive });
    updateUserLocal(user.id, { isActive: !user.isActive });

    // Success notification
    const successDiv = document.createElement("div");
    successDiv.className =
      "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2";
    successDiv.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
      Utilisateur ${!user.isActive ? "activé" : "désactivé"} avec succès !
    `;
    document.body.appendChild(successDiv);
    setTimeout(() => successDiv.remove(), 3000);
  };

  const toggleVerified = async (user) => {
    // CORRECTION ICI: utiliser emailIsVerified
    const userRef = doc(db, "users", user.id);
    await updateDoc(userRef, { emailIsVerified: !user.emailIsVerified });
    updateUserLocal(user.id, { emailIsVerified: !user.emailIsVerified });

    // Success notification
    const successDiv = document.createElement("div");
    successDiv.className =
      "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2";
    successDiv.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
      Statut de vérification mis à jour !
    `;
    document.body.appendChild(successDiv);
    setTimeout(() => successDiv.remove(), 3000);
  };

  const formatTimestamp = (ts) =>
    ts && ts.toDate ? moment(ts.toDate()).format("DD/MM/YY HH:mm") : "-";

  // CORRECTION ICI: utiliser emailIsVerified
  const verifiedCount = allUsers.filter((u) => u.emailIsVerified).length;
  const activeCount = allUsers.filter((u) => u.isActive).length;

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="max-w-[95%] mx-auto space-y-6">
          {/* Info Card */}
          <Card className="bg-gradient-to-r from-red-50 to-rose-50 border-red-200">
            <CardContent className="flex items-start gap-4">
              <div className="p-3 bg-[#801111] rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 text-white"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Gestion des utilisateurs
                </h3>
                <p className="text-sm text-gray-600">
                  {allUsers.length} utilisateur{allUsers.length > 1 ? "s" : ""}{" "}
                  enregistré{allUsers.length > 1 ? "s" : ""} · {verifiedCount}{" "}
                  vérifié{verifiedCount > 1 ? "s" : ""} · {activeCount} actif
                  {activeCount > 1 ? "s" : ""}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total</p>
                  <p className="text-3xl font-bold text-[#801111]">
                    {allUsers.length}
                  </p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8 text-[#801111]"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Vérifiés</p>
                  <p className="text-3xl font-bold text-green-600">
                    {verifiedCount}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8 text-green-600"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Actifs</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {activeCount}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8 text-blue-600"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Affichés</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {filteredUsers.length}
                  </p>
                </div>
                <div className="p-3 bg-gray-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8 text-gray-600"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Search */}
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rechercher
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Nom, email, ville..."
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pl-10 focus:outline-none focus:ring-2 focus:ring-[#801111] focus:border-transparent"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      autoComplete="off"
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5 text-gray-400 absolute left-3 top-3"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                      />
                    </svg>
                  </div>
                </div>

                {/* Filter Verified */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vérification
                  </label>
                  <select
                    value={filterVerified}
                    onChange={(e) => setFilterVerified(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#801111] focus:border-transparent"
                  >
                    <option value="all">Tous</option>
                    <option value="verified">Vérifiés</option>
                    <option value="notVerified">Non vérifiés</option>
                  </select>
                </div>

                {/* Filter Active */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut
                  </label>
                  <select
                    value={filterActive}
                    onChange={(e) => setFilterActive(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#801111] focus:border-transparent"
                  >
                    <option value="all">Tous</option>
                    <option value="active">Actifs</option>
                    <option value="inactive">Inactifs</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Users Table */}
          <Card>
            <CardHeader>
              <CardTitle>
                {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? "s" : ""}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {currentUsers.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-8 h-8 text-gray-400"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-600 font-medium mb-1">
                    Aucun utilisateur trouvé
                  </p>
                  <p className="text-sm text-gray-500">
                    Aucun utilisateur ne correspond aux filtres sélectionnés
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                          Profil
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                          Nom
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                          Email
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                          Téléphone
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                          Ville
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                          Type
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                          Créé le
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                          Statut
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentUsers.map((user) => (
                        <tr
                          key={user.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-3 py-3">
                            {user.photo ? (
                              <img
                                src={user.photo}
                                alt="Profil"
                                className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-200"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-gradient-to-br from-[#801111] to-[#5a0c0c] rounded-full flex items-center justify-center text-white font-semibold">
                                {user.firstname?.[0]?.toUpperCase() || "?"}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-3">
                            <div>
                              <p className="font-medium text-gray-900">
                                {user.firstname} {user.lastname}
                              </p>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-gray-600">
                            {user.email}
                          </td>
                          <td className="px-3 py-3 text-gray-600">
                            {user.phoneNumber || "-"}
                          </td>
                          <td className="px-3 py-3 text-gray-600">
                            {user.city || "-"}
                          </td>
                          <td className="px-3 py-3">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {user.type || "-"}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-gray-600 whitespace-nowrap">
                            {formatTimestamp(user.createdAt)}
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex flex-col gap-1">
                              {user.emailIsVerified && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  ✓ Vérifié
                                </span>
                              )}
                              {user.isActive && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  ● Actif
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex flex-col gap-2">
                              <button
                                onClick={() => navigate(`/users/${user.id}`)}
                                className="px-3 py-1 rounded-lg bg-[#801111] text-white hover:bg-[#5a0c0c] transition text-xs font-medium"
                              >
                                Voir
                              </button>
                              <button
                                onClick={() => toggleActive(user)}
                                className={`px-3 py-1 rounded-lg text-white transition text-xs font-medium ${
                                  user.isActive
                                    ? "bg-red-500 hover:bg-red-600"
                                    : "bg-green-500 hover:bg-green-600"
                                }`}
                              >
                                {user.isActive ? "Désactiver" : "Activer"}
                              </button>
                              <button
                                onClick={() => toggleVerified(user)}
                                className={`px-3 py-1 rounded-lg text-white transition text-xs font-medium ${
                                  user.emailIsVerified
                                    ? "bg-orange-500 hover:bg-orange-600"
                                    : "bg-green-600 hover:bg-green-700"
                                }`}
                              >
                                {user.emailIsVerified ? "Retirer vérif." : "Vérifier"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {pageCount > 1 && (
                <div className="flex justify-center items-center mt-6 gap-4">
                  <button
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    disabled={page === 1}
                    className="px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                  >
                    ← Précédent
                  </button>
                  <span className="font-semibold text-gray-700">
                    Page {page} / {pageCount}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(p + 1, pageCount))}
                    disabled={page === pageCount}
                    className="px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                  >
                    Suivant →
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      }
    />
  );
}